import React, { useEffect, useRef, useState } from "react";
import { signInWithEmailAndPassword, onAuthStateChanged } from "firebase/auth";
import { auth } from "../lib/firebase";
import {
  Mail,
  Lock,
  LogIn,
  UserPlus,
  User,
  X,
  ArrowRight,
  Sparkles,
  Loader2,
  ShieldCheck,
} from "lucide-react";

function cx(...s) {
  return s.filter(Boolean).join(" ");
}

const REGISTER_GATE_PASSWORD = "MUDIF-ACCESS-2026"; 

function WelcomeModal({ open, name, onClose }) {
  const [pct, setPct] = useState(0);
  const [completed, setCompleted] = useState(false);
  const rafRef = useRef(0);
  const startRef = useRef(0);
  const doneRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setPct(0);
      setCompleted(false);
      doneRef.current = false;
      cancelAnimationFrame(rafRef.current);
      return;
    }

    startRef.current = performance.now();
    const tick = (t) => {
      const dt = t - startRef.current;
      const p = Math.min(100, Math.round((dt / 1700) * 100));
      setPct(p);
      if (p < 100) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafRef.current);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (pct < 100) return;
    if (doneRef.current) return;

    doneRef.current = true;
    setCompleted(true);

    const t = setTimeout(() => onClose(), 1200);
    return () => clearTimeout(t);
  }, [pct, open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 sm:p-6">
      <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-md" />

      <div
        className={cx(
          "relative w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-[#fcfbfa] shadow-[0_50px_180px_rgba(2,6,23,.42)]",
          "transition-transform duration-500",
          completed ? "scale-[1.012]" : "scale-100"
        )}
      >
        <div className="pointer-events-none absolute inset-0 opacity-95 [background:radial-gradient(760px_320px_at_10%_0%,rgba(204,58,33,.16),transparent_60%),radial-gradient(760px_320px_at_90%_120%,rgba(251,191,36,.10),transparent_62%)]" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.05] [background-image:linear-gradient(to_right,rgba(2,6,23,.10)_1px,transparent_1px),linear-gradient(to_bottom,rgba(2,6,23,.07)_1px,transparent_1px)] [background-size:72px_72px]" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1.5 bg-[linear-gradient(90deg,#CC3A21,#E58E7C,#F2C9A5)]" />

        <div className="relative p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1 text-[11px] font-black text-white shadow">
                <Sparkles className="h-4 w-4" />
                Accesso riuscito
              </div>

              <h3 className="mt-3 text-xl font-black tracking-tight text-slate-900">
                Benvenuto{name ? `, ${name}` : ""} 👋
              </h3>
              <p className="mt-1 text-sm font-semibold text-slate-600">
                {completed
                  ? "Perfetto. Apro il pannello admin…"
                  : "Sto preparando il pannello admin…"}
              </p>
            </div>

            <button
              onClick={onClose}
              className="rounded-xl p-2 hover:bg-slate-100"
              aria-label="Chiudi"
              title="Vai all’Admin"
            >
              <X className="h-5 w-5 text-slate-700" />
            </button>
          </div>

          <div className="mt-5 rounded-2xl border border-slate-200 bg-white/80 p-4">
            <div className="flex items-center gap-3">
              <span
                className={cx(
                  "grid h-10 w-10 place-items-center rounded-2xl text-white shadow",
                  completed ? "bg-emerald-600" : "bg-slate-900"
                )}
              >
                {completed ? (
                  <ShieldCheck className="h-5 w-5" />
                ) : (
                  <Loader2 className="h-5 w-5 animate-spin" />
                )}
              </span>

              <div className="min-w-0">
                <div className="text-sm font-black text-slate-900">
                  {completed ? "Pronto ✅" : "Verifica sessione"}
                </div>
                <div className="text-xs font-semibold text-slate-600">
                  {completed
                    ? "Redirect in corso…"
                    : "Token attivo · Controllo accesso…"}
                </div>
              </div>

              <div className="ml-auto text-xs font-black text-slate-700 tabular-nums">
                {pct}%
              </div>
            </div>

            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full rounded-full transition-[width] duration-150"
                style={{
                  width: `${pct}%`,
                  backgroundImage:
                    "linear-gradient(90deg,#CC3A21,#E58E7C,#F2C9A5,#CC3A21)",
                  backgroundSize: "320% 100%",
                  backgroundPosition: `${pct}% 0%`,
                }}
              />
            </div>

            <div className="mt-3 text-[11px] font-semibold text-slate-600">
              {completed ? "Ci siamo ✨" : "Puoi entrare subito premendo “Vai all’Admin”."}
            </div>
          </div>

          <button
            onClick={onClose}
            className={cx(
              "mt-5 w-full rounded-2xl py-3 text-sm font-black text-white shadow-lg transition",
              completed
                ? "bg-emerald-600 hover:bg-emerald-700"
                : "bg-slate-900 hover:bg-black"
            )}
          >
            Vai all’Admin
          </button>
        </div>
      </div>
    </div>
  );
}

function RegisterGateModal({
  open,
  password,
  setPassword,
  onClose,
  onUnlock,
  loading = false,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 sm:p-6">
      <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-md" />
      <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-[#fcfbfa] shadow-[0_50px_180px_rgba(2,6,23,.38)]">
        <div className="pointer-events-none absolute inset-0 opacity-95 [background:radial-gradient(760px_320px_at_10%_0%,rgba(204,58,33,.14),transparent_60%),radial-gradient(760px_320px_at_90%_120%,rgba(251,191,36,.10),transparent_62%)]" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1.5 bg-[linear-gradient(90deg,#CC3A21,#E58E7C,#F2C9A5)]" />

        <div className="relative p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#CC3A21]/20 bg-[#CC3A21]/[0.06] px-3 py-1 text-[11px] font-black text-[#7A2417]">
                <Sparkles className="h-4 w-4" />
                Accesso protetto
              </div>
              <h3 className="mt-3 text-xl font-black tracking-tight text-slate-900">
                Area registrazione protetta
              </h3>
              <p className="mt-1 text-sm font-semibold text-slate-600">
                Inserisci la password interna per visualizzare il contenuto.
              </p>
            </div>

            <button
              onClick={onClose}
              className="rounded-xl p-2 hover:bg-slate-100"
              aria-label="Chiudi"
            >
              <X className="h-5 w-5 text-slate-700" />
            </button>
          </div>

          <div className="mt-5 relative">
            <Lock className="absolute left-3 top-3.5 h-5 w-5 text-slate-400" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:border-[#CC3A21]/40 focus:ring-4 focus:ring-[#CC3A21]/10"
              placeholder="Password area registrazione"
              autoComplete="off"
            />
          </div>

          <div className="mt-5 flex gap-3">
            <button
              onClick={onClose}
              className="w-1/2 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-black text-slate-900 shadow-sm transition hover:bg-slate-50"
            >
              Annulla
            </button>
            <button
              onClick={onUnlock}
              disabled={loading || !password}
              className="w-1/2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-black text-white shadow-lg transition hover:bg-black disabled:opacity-60"
            >
              {loading ? "Verifica..." : "Apri"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPass, setLoginPass] = useState("");

  const [regUsername, setRegUsername] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPass, setRegPass] = useState("");

  const [activeTab, setActiveTab] = useState("login");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [welcomeName, setWelcomeName] = useState("");

  const [registerGateOpen, setRegisterGateOpen] = useState(false);
  const [registerGatePassword, setRegisterGatePassword] = useState("");
  const [registerUnlocked, setRegisterUnlocked] = useState(false);

  const suppressAutoRedirectRef = useRef(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u) return;
      if (suppressAutoRedirectRef.current) return;
      if (welcomeOpen) return;
      window.location.href = "/admin";
    });

    return () => unsub();
  }, [welcomeOpen]);

  const goAdmin = () => {
    setWelcomeOpen(false);
    suppressAutoRedirectRef.current = false;
    window.location.href = "/admin";
  };

  const login = async () => {
    try {
      setMsg("");
      setLoading(true);

      suppressAutoRedirectRef.current = true;

      const cred = await signInWithEmailAndPassword(auth, loginEmail, loginPass);
      const dn = (cred.user.displayName || "").trim();
      setWelcomeName(dn || "Admin");
      setWelcomeOpen(true);
    } catch (e) {
      suppressAutoRedirectRef.current = false;
      setMsg("Errore login: " + (e?.message || "sconosciuto"));
    } finally {
      setLoading(false);
    }
  };

  const unlockRegisterArea = async () => {
    setMsg("");

    if (registerGatePassword !== REGISTER_GATE_PASSWORD) {
      setMsg("Password registrazione non corretta.");
      return;
    }

    setRegisterUnlocked(true);
    setRegisterGateOpen(false);
    setRegisterGatePassword("");
  };

  return (
    <main className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 py-10 bg-[radial-gradient(ellipse_at_top,rgba(204,58,33,.18),transparent_55%),linear-gradient(180deg,#f6f2ee_0%,#fcfbfa_35%,#f8fafc_100%)]">
      <div className="absolute w-[520px] h-[520px] bg-[#CC3A21]/20 blur-[120px] rounded-full top-[-120px] left-[-160px]" />
      <div className="absolute w-[560px] h-[560px] bg-amber-200/25 blur-[120px] rounded-full bottom-[-140px] right-[-160px]" />
      <div className="absolute inset-0 opacity-[0.06] [background-image:radial-gradient(circle_at_1px_1px,rgba(15,23,42,.55)_1px,transparent_0)] [background-size:24px_24px]" />

      <div className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-slate-200 bg-white/90 shadow-[0_30px_120px_rgba(2,6,23,.16)] backdrop-blur-2xl">
        <div className="pointer-events-none absolute inset-0 opacity-90 [background:radial-gradient(760px_320px_at_10%_0%,rgba(204,58,33,.10),transparent_60%),radial-gradient(760px_320px_at_90%_120%,rgba(251,191,36,.08),transparent_62%)]" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1.5 bg-[linear-gradient(90deg,#CC3A21,#E58E7C,#F2C9A5)]" />

        <div className="relative p-6 sm:p-8">
          <div className="text-center">
            <div className="mx-auto inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 ring-1 ring-slate-900/10 shadow">
              <span className="grid h-7 w-7 place-items-center rounded-xl bg-slate-900 text-white">
                <Sparkles className="h-4 w-4" />
              </span>
              <span className="text-xs font-black tracking-tight text-slate-900">
                MuDiF · Admin
              </span>
            </div>

            <h1 className="mt-4 text-3xl sm:text-4xl font-black tracking-tight text-slate-900">
              Admin Access
            </h1>
            <p className="mt-2 text-sm font-semibold text-slate-600">
              Accesso al pannello MuDiF.
            </p>
          </div>

          <div className="mt-6 grid grid-cols-2 rounded-2xl border border-slate-200 bg-white p-1">
            <button
              onClick={() => {
                setActiveTab("login");
                setMsg("");
              }}
              className={cx(
                "rounded-2xl py-2.5 text-sm font-black transition",
                activeTab === "login"
                  ? "bg-slate-900 text-white shadow"
                  : "text-slate-700 hover:bg-slate-50"
              )}
            >
              Entra
            </button>
            <button
              onClick={() => {
                setMsg("");
                if (registerUnlocked) {
                  setActiveTab("register");
                } else {
                  setRegisterGateOpen(true);
                }
              }}
              className={cx(
                "rounded-2xl py-2.5 text-sm font-black transition",
                activeTab === "register"
                  ? "bg-slate-900 text-white shadow"
                  : "text-slate-700 hover:bg-slate-50"
              )}
            >
              Registrati
            </button>
          </div>

          <div className="mt-6">
            {activeTab === "login" && (
              <div className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3 top-3.5 h-5 w-5 text-slate-400" />
                  <input
                    className="w-full rounded-2xl border border-slate-300 bg-white/95 pl-10 pr-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:border-[#CC3A21]/40 focus:ring-4 focus:ring-[#CC3A21]/10"
                    placeholder="Email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    inputMode="email"
                    autoComplete="email"
                  />
                </div>

                <div className="relative">
                  <Lock className="absolute left-3 top-3.5 h-5 w-5 text-slate-400" />
                  <input
                    type="password"
                    className="w-full rounded-2xl border border-slate-300 bg-white/95 pl-10 pr-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:border-[#CC3A21]/40 focus:ring-4 focus:ring-[#CC3A21]/10"
                    placeholder="Password"
                    value={loginPass}
                    onChange={(e) => setLoginPass(e.target.value)}
                    autoComplete="current-password"
                  />
                </div>

                <button
                  onClick={login}
                  disabled={loading || !loginEmail || !loginPass}
                  className={cx(
                    "w-full flex items-center justify-center gap-2 rounded-2xl py-3.5 font-black shadow-lg transition",
                    "bg-slate-900 text-white hover:bg-black",
                    "disabled:opacity-60 disabled:hover:bg-slate-900"
                  )}
                >
                  <LogIn className="h-4.5 w-4.5" />
                  {loading ? "Accesso..." : "Entra"}
                  <ArrowRight className="h-4 w-4 opacity-80" />
                </button>
              </div>
            )}

            {activeTab === "register" && registerUnlocked && (
              <div className="space-y-4">
                <div className="rounded-2xl border border-[#CC3A21]/15 bg-[#CC3A21]/[0.05] p-4 text-xs font-semibold leading-relaxed text-slate-700">
                  Area registrazione sbloccata. Il contenuto è ora visibile perché hai inserito la password corretta.
                </div>

                <div className="relative">
                  <User className="absolute left-3 top-3.5 h-5 w-5 text-slate-400" />
                  <input
                    className="w-full rounded-2xl border border-slate-300 bg-white/95 pl-10 pr-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:border-[#CC3A21]/40 focus:ring-4 focus:ring-[#CC3A21]/10"
                    placeholder="Username"
                    value={regUsername}
                    onChange={(e) => setRegUsername(e.target.value)}
                    autoComplete="nickname"
                  />
                </div>

                <div className="relative">
                  <Mail className="absolute left-3 top-3.5 h-5 w-5 text-slate-400" />
                  <input
                    className="w-full rounded-2xl border border-slate-300 bg-white/95 pl-10 pr-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:border-[#CC3A21]/40 focus:ring-4 focus:ring-[#CC3A21]/10"
                    placeholder="Email"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    inputMode="email"
                    autoComplete="email"
                  />
                </div>

                <div className="relative">
                  <Lock className="absolute left-3 top-3.5 h-5 w-5 text-slate-400" />
                  <input
                    type="password"
                    className="w-full rounded-2xl border border-slate-300 bg-white/95 pl-10 pr-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:border-[#CC3A21]/40 focus:ring-4 focus:ring-[#CC3A21]/10"
                    placeholder="Password"
                    value={regPass}
                    onChange={(e) => setRegPass(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>

                

                <button
  onClick={() => {
    setMsg("Registrazione sbloccata. Qui puoi collegare la tua logica createUser.");
  }}
  disabled={loading || !regEmail || !regPass}
  className={cx(
    "w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-black shadow-lg transition",
    "bg-slate-900 text-white hover:bg-black",
    "disabled:opacity-60 disabled:hover:bg-slate-900"
  )}
>
  <UserPlus className="h-4.5 w-4.5" />
  {loading ? "Registrazione..." : "Registrati"}
  <ArrowRight className="h-4 w-4 opacity-80" />
</button>
              </div>
            )}
          </div>

          {msg && (
            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-100 py-3 px-4 text-center text-sm font-semibold text-slate-800">
              {msg}
            </div>
          )}
        </div>
      </div>

      <WelcomeModal open={welcomeOpen} name={welcomeName} onClose={goAdmin} />

      <RegisterGateModal
        open={registerGateOpen}
        password={registerGatePassword}
        setPassword={setRegisterGatePassword}
        onClose={() => setRegisterGateOpen(false)}
        onUnlock={unlockRegisterArea}
      />
    </main>
  );
}